// Slug / display name, plus the optional website + branding extraction row.
// Controlled and stateless — the caller owns the values and supplies its own
// <Section> wrapper, so the internal onboarding form and the publisher-facing
// intake can word their copy differently. The internal (Publica-matching)
// name lives in PublisherForm's Products card (CTV-745), not here.

import React from 'react';
import { Loader2, Sparkles } from 'lucide-react';
import { inputClass, readonlyInputClass, labelClass } from './publisherFieldStyles';
import type { IdentityValues } from './types';

interface Props {
  values: IdentityValues;
  onChange: (patch: Partial<IdentityValues>) => void;
  /** Slug is immutable once a station exists. */
  slugReadOnly?: boolean;
  /**
   * The website row drives POST /stations/extract-branding, which is
   * admin-only. Publishers upload their own logo instead.
   */
  showWebsiteExtract?: boolean;
  extracting?: boolean;
  onExtract?: () => void;
}

const IdentityFields: React.FC<Props> = ({
  values,
  onChange,
  slugReadOnly = false,
  showWebsiteExtract = true,
  extracting = false,
  onExtract,
}) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
    <div>
      <label className={labelClass}>Slug (URL key)</label>
      <input
        required
        value={values.slug}
        onChange={(e) => onChange({ slug: e.target.value })}
        placeholder="e.g. wisc"
        className={slugReadOnly ? readonlyInputClass : inputClass}
        readOnly={slugReadOnly}
      />
    </div>
    <div>
      <label className={labelClass}>User-Facing Station/Channel Name</label>
      <input
        value={values.displayName}
        onChange={(e) => onChange({ displayName: e.target.value })}
        placeholder="Channel 3000"
        className={inputClass}
      />
    </div>
    {/* TODO: persist the website URL on `stations` (needs migration:
        add a `website` column, write it on create/update, and roundtrip
        it through PublisherConfig). Pending product discussion. */}
    {showWebsiteExtract && (
      <div className="sm:col-span-2">
        <label className={labelClass}>
          Website URL <span className="font-normal normal-case tracking-normal text-slate-400">(used to extract branding — not stored)</span>
        </label>
        <div className="flex gap-2">
          <input
            value={values.website}
            onChange={(e) => onChange({ website: e.target.value })}
            placeholder="https://www.channel3000.com"
            className={`${inputClass} flex-1`}
          />
          <button
            type="button"
            onClick={onExtract}
            disabled={extracting}
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-[13px] font-semibold text-white bg-slate-900 hover:bg-slate-700 disabled:opacity-60 transition-colors"
          >
            {extracting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {extracting ? 'Extracting…' : 'Extract branding'}
          </button>
        </div>
      </div>
    )}
  </div>
);

export default IdentityFields;
